import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import React, { useEffect, useState } from 'react';
import customersService from '../../service/api/CustomersService';
import FormErrorMessage from '../_shared/FormErrorMessage';
import FormErrorMessageScroller from '../_shared/FormErrorMessageScroller';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../types/Customer';

import WriteOnlyButton from '../controls/WriteOnlyButton';

interface FormValuesProps extends Customer {}

const INIT_FORM_STATE: FormValuesProps = {
  id: null,
  name: '',
  firstName: '',
  lastName: '',
  taxId: '',
  comment: '',
};

type Props = {
  dialogVisible: boolean;
  closeAddEditModal: VoidFunction;
  customer: Customer;

  showAddToast: (name: string) => void;
  showSaveToast: (name: string) => void;
};

function CustomerDialog({
  dialogVisible,
  closeAddEditModal,
  customer,

  showAddToast,
  showSaveToast,
}: Props) {
  const [initFormValues, setInitFormValues] = useState(INIT_FORM_STATE);

  const hideDialog = () => {
    formik.resetForm({ values: INIT_FORM_STATE });
    closeAddEditModal();
  };

  const { t } = useTranslation();

  useEffect(() => {
    if (dialogVisible && customer) {
      setInitFormValues({
        id: customer.id,
        name: customer.name,
        firstName: customer.firstName,
        lastName: customer.lastName,
        taxId: customer.taxId,
        comment: customer.comment,
      });
    } else {
      setInitFormValues(INIT_FORM_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible]);

  const formik = useFormik({
    initialValues: initFormValues,
    validate: (data: FormValuesProps) => {
      let errors: FormikErrors<FormValuesProps> = {};

      if (!data.name?.trim()) {
        errors.name = t('common.error_name_required');
      }

      return errors;
    },
    onSubmit: async (formData: FormValuesProps, helpers: FormikHelpers<FormValuesProps>) => {
      try {
        if (!formData.id) {
          await customersService.create(formData);

          showAddToast(formData.name);
        } else {
          await customersService.edit(formData, formData.id);

          showSaveToast(formData.name);
        }

        hideDialog();
      } catch (error: any) {
        if (error.response.status === 409) {
          const field = error.response.data.field || 'name';
          helpers.setFieldError(field, t(`customers.error_${field}_already_taken`));
        }
      }
      helpers.setSubmitting(false);
    },
    enableReinitialize: true,
  });

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <WriteOnlyButton
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={formik.submitForm}
      />
    </>
  );

  return (
    <FormErrorMessageScroller formikInstance={formik}>
      <Dialog
        visible={dialogVisible}
        header={t('customers.customer_details')}
        modal
        className="p-fluid"
        footer={DialogFooter}
        onHide={hideDialog}
        breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
        style={{ width: '40vw' }}
      >
        <form>
          <div className="col-10">
            <div className="field">
              <label htmlFor="name">{t('common.name')}</label>
              <InputText
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                className={formik.touched.name && formik.errors.name && 'p-invalid'}
              />
              <FormErrorMessage fieldName="name" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="firstName">{t('customers.first_name')}</label>
              <InputText
                id="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                className={formik.touched.firstName && formik.errors.firstName && 'p-invalid'}
              />
              <FormErrorMessage fieldName="firstName" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="lastName">{t('customers.last_name')}</label>
              <InputText
                id="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                className={formik.touched.lastName && formik.errors.lastName && 'p-invalid'}
              />
              <FormErrorMessage fieldName="lastName" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="taxId">{t('customers.tax_id')}</label>
              <InputText
                id="taxId"
                value={formik.values.taxId}
                onChange={formik.handleChange}
                className={formik.touched.taxId && formik.errors.taxId && 'p-invalid'}
              />
              <FormErrorMessage fieldName="taxId" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="comment">{t('customers.comment')}</label>
              <InputTextarea
                autoResize
                id="comment"
                value={formik.values.comment}
                onChange={formik.handleChange}
                className={formik.touched.comment && formik.errors.comment && 'p-invalid'}
              />
              <FormErrorMessage fieldName="comment" formikInstance={formik} />
            </div>
          </div>
        </form>
      </Dialog>
    </FormErrorMessageScroller>
  );
}

export default CustomerDialog;
